/**
 * Gate arithmetic (+ × − ÷) and breakeven helpers for binary pairs.
 */
/**
 * Apply a single gate operator to unit count N.
 * - add: N + K
 * - mul: N * K (integer)
 * - sub: max(0, N - K)
 * - div: floor(N / K); K must be >= 1
 */
export function applyGateOp(n, op, k) {
    const N = Math.max(0, Math.floor(n));
    const K = Math.floor(k);
    switch (op) {
        case "add":
            return N + Math.max(0, K);
        case "mul":
            return N * Math.max(0, K);
        case "sub":
            return Math.max(0, N - Math.max(0, K));
        case "div": {
            const d = Math.max(1, K);
            return Math.floor(N / d);
        }
        default: {
            const _exhaustive = op;
            return _exhaustive;
        }
    }
}
/**
 * Breakeven N for +A vs ×M: choose + when N < A/(M-1).
 * Returns the threshold (exclusive upper bound for preferring +).
 * If M <= 1, multiply never grows; prefer + always → +Infinity.
 */
export function breakevenAddVsMul(a, m) {
    const A = a;
    const M = m;
    if (M <= 1)
        return Number.POSITIVE_INFINITY;
    return A / (M - 1);
}
/** Prefer + when N < breakeven; × when N > breakeven; either when equal. */
export function preferAddOverMul(n, a, m) {
    return n < breakevenAddVsMul(a, m);
}
/**
 * Best choice for binary +A vs ×M given current N.
 * At exact breakeven both yield the same result → "tie".
 */
export function chooseAddVsMul(n, a, m) {
    const afterAdd = applyGateOp(n, "add", a);
    const afterMul = applyGateOp(n, "mul", m);
    if (afterAdd > afterMul)
        return "add";
    if (afterMul > afterAdd)
        return "mul";
    return "tie";
}
/** Evaluate which of two gates yields higher N. */
export function betterGate(n, left, right) {
    const l = applyGateOp(n, left.op, left.k);
    const r = applyGateOp(n, right.op, right.k);
    if (l > r)
        return "left";
    if (r > l)
        return "right";
    return "tie";
}
//# sourceMappingURL=gates.js.map